import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Users, Map, Gift, CheckCircle, Star, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { features, stats, reports } from '../mock';

const iconMap = {
  Calendar,
  Users,
  Map,
  Gift
};

const Home = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [comment, setComment] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submittedComments, setSubmittedComments] = useState([]);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      const subscribers = JSON.parse(localStorage.getItem('subscribers') || '[]');
      if (!subscribers.includes(email)) {
        subscribers.push(email);
        localStorage.setItem('subscribers', JSON.stringify(subscribers));
        toast({
          title: 'Successfully subscribed!',
          description: 'You\'ll receive our bi-weekly reports straight to your inbox.'
        });
        setEmail('');
      } else {
        toast({
          title: 'Already subscribed',
          description: 'This email is already on our list.'
        });
      }
    }
  };

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    const comments = JSON.parse(localStorage.getItem('comments') || '[]');
    const newComment = {
      ...comment,
      id: Date.now(),
      date: new Date().toISOString()
    };
    comments.push(newComment);
    localStorage.setItem('comments', JSON.stringify(comments));
    setSubmittedComments([newComment, ...submittedComments]);
    
    toast({
      title: 'Comment submitted!',
      description: 'Thank you for sharing your thoughts with us.'
    });
    
    setComment({
      name: '',
      email: '',
      message: ''
    });
  };

  const handleCommentChange = (e) => {
    setComment({
      ...comment,
      [e.target.name]: e.target.value
    });
  };

  const latestReports = reports.slice(0, 3);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-20 pb-24 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-white to-orange-50 opacity-70" />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="flex items-center gap-3 mb-6 justify-center">
            <Star className="w-6 h-6 text-[#A0704E] fill-[#A0704E]" />
            <span className="text-[#A0704E] font-medium tracking-wide">Building for Adaptation</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 text-center">
            Navigate the Australian Market,
            <br />
            <span className="text-[#A0704E]">One Touchpoint at a Time</span>
          </h1>
          <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto text-center leading-relaxed">
            Fresh insights from marketing students, backed by research. Bi-weekly industry reports that guide brands and students through every customer touchpoint in the Australian market.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/reports">
              <Button size="lg" className="bg-[#A0704E] hover:bg-[#8B5F3E] text-white px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
                Explore Reports <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-[#A0704E] text-[#A0704E] hover:bg-[#A0704E] hover:text-white px-8 py-6 text-lg transition-all duration-300">
                Get Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-[#A0704E]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-amber-100 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Makes Marketry Different</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We bridge the gap between academic insight and practical marketing strategy for the Australian market
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <Card key={feature.id} className="border-2 border-gray-100 hover:border-[#A0704E] hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-[#A0704E]" />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600">{feature.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Latest Reports Preview */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-amber-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Latest Industry Insights</h2>
            <p className="text-lg text-gray-600">Fresh perspectives on Australian market touchpoints</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-10">
            {latestReports.map((report) => (
              <Card key={report.id} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardHeader>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-[#A0704E] bg-amber-50 px-3 py-1 rounded-full">
                      {report.industry}
                    </span>
                    <span className="text-xs text-gray-500">{new Date(report.date).toLocaleDateString('en-AU')}</span>
                  </div>
                  <CardTitle className="text-lg leading-tight">{report.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-4">{report.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{report.readTime}</span>
                    <Link to="/reports">
                      <Button variant="ghost" size="sm" className="text-[#A0704E] hover:text-[#8B5F3E]">
                        Read More <ArrowRight className="ml-1 w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center">
            <Link to="/reports">
              <Button size="lg" variant="outline" className="border-2 border-[#A0704E] text-[#A0704E] hover:bg-[#A0704E] hover:text-white">
                View All Reports
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-lg text-gray-600">Simple steps to unlock Australian market insights</p>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#A0704E] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">1</div>
              <h3 className="text-xl font-bold mb-3">Subscribe for Free</h3>
              <p className="text-gray-600">Get access to all bi-weekly reports during our trial period</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#A0704E] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">2</div>
              <h3 className="text-xl font-bold mb-3">Receive Insights</h3>
              <p className="text-gray-600">Every two weeks, get a deep dive into a different Australian industry</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#A0704E] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">3</div>
              <h3 className="text-xl font-bold mb-3">Apply & Grow</h3>
              <p className="text-gray-600">Use touchpoint strategies in your projects or business campaigns</p>
            </div>
          </div>
        </div>
      </section>

      {/* Comments Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-amber-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How do you think of my work?</h2>
            <p className="text-lg text-gray-600">Leave your comment here</p>
          </div>
          
          <Card className="border-2 border-gray-100 mb-8">
            <CardContent className="pt-8">
              <form onSubmit={handleCommentSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="comment-name" className="text-base">Name</Label>
                    <Input
                      id="comment-name"
                      name="name"
                      type="text"
                      placeholder="Your name"
                      value={comment.name}
                      onChange={handleCommentChange}
                      required
                      className="mt-2 py-6 text-base border-2 border-gray-200 focus:border-[#A0704E]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="comment-email" className="text-base">Email</Label>
                    <Input
                      id="comment-email"
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      value={comment.email}
                      onChange={handleCommentChange}
                      required
                      className="mt-2 py-6 text-base border-2 border-gray-200 focus:border-[#A0704E]"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="comment-message" className="text-base">Your Comment</Label>
                  <Textarea
                    id="comment-message"
                    name="message"
                    placeholder="Share your thoughts about Marketry..."
                    value={comment.message}
                    onChange={handleCommentChange}
                    required
                    rows={5}
                    className="mt-2 text-base border-2 border-gray-200 focus:border-[#A0704E] resize-none"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full md:w-auto bg-[#A0704E] hover:bg-[#8B5F3E] text-white px-8 py-6 text-lg"
                >
                  Submit Comment <Send className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Display submitted comments */}
          {submittedComments.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Recent Comments</h3>
              {submittedComments.map((c) => (
                <Card key={c.id} className="border-2 border-gray-100">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#A0704E] text-white rounded-full flex items-center justify-center font-semibold flex-shrink-0">
                        {c.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-gray-900">{c.name}</span>
                          <span className="text-xs text-gray-500">
                            {new Date(c.date).toLocaleDateString('en-AU')}
                          </span>
                        </div>
                        <p className="text-gray-700">{c.message}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#A0704E] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Navigate the Australian Market?</h2>
          <p className="text-xl text-amber-100 mb-8">
            Subscribe now and get bi-weekly insights delivered to your inbox
          </p>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-white text-gray-900 border-0 py-6 text-lg"
            />
            <Button type="submit" size="lg" className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-6 text-lg whitespace-nowrap">
              Subscribe Free
            </Button>
          </form>
          <p className="text-amber-100 text-sm mt-4">Free during trial period. No credit card required.</p>
        </div>
      </section>
    </div>
  );
};

export default Home;